import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, ErrorScreen, Header } from '@paysense-india-services/elevate-web'

import { PAY_API, TRANSACT_API } from '@/constants/api'

import { useFetch } from '@/hooks/useFetch'

import apiService from '@/lib/apiUtils'

import PageLoader from '@/components/PageLoader/index'

import RepaymentAmountList from '@/app/pages/pay/components/RepaymentAmountList'
import ReviewPaymentBottomSheet from '@/app/pages/pay/components/ReviewPaymentBottomSheet'
import {
  getPaymentReturnUrl,
  getPayuFormFieldKeys,
  getReviewSubScreenKey,
  processTransactResponse
} from '@/app/pages/pay/utils'

const PayPage = () => {
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [selectedRepayment, setSelectedRepayment] = useState(null)
  const [transactData, setTransactData] = useState(null)
  const [isTransacting, setIsTransacting] = useState(false)

  const navigate = useNavigate()
  const paymentFormRef = useRef(null)

  const { data: pageData, loading, error } = useFetch(PAY_API, false, true)

  const { screen: data, subScreen } = pageData || {}

  const handleRepaymentSelection = (item, value) => {
    setSelectedRepayment({ item, value })
  }

  const handlePay = async () => {
    const loans = selectedRepayment?.item?.meta?.loans || []

    const paymentOptions = loans.map(loan => ({
      loan_id: loan.id,
      payment_type: loan.payment_type,
      late_charges: false
    }))

    if (!paymentOptions.length) return

    setIsTransacting(true)

    try {
      const response = await apiService.post(TRANSACT_API, {
        body: {
          payment_options: paymentOptions,
          return_url: getPaymentReturnUrl(),
          source: 'web'
        }
      })

      const transactionData = response?.data ?? response
      setTransactData(transactionData)
    } catch (err) {
      showSnackbar(err?.message || 'Something went wrong')
    } finally {
      setIsTransacting(false)
    }
  }

  useEffect(() => {
    if (!transactData) return

    const result = processTransactResponse(transactData, navigate)

    if (result.type === 'payment_url' && paymentFormRef.current) {
      paymentFormRef.current.submit()
    }
  }, [transactData, navigate])

  const handleContinue = () => {
    setShowReviewModal(true)
  }

  if (loading || isTransacting) {
    return <PageLoader isLoading={loading || isTransacting} />
  }

  if (error) {
    return <ErrorScreen description='Something went wrong' title='Error' />
  }

  return (
    <>
      <Header
        className='ew-bg-secondary ew-shadow-none'
        isBackButton={true}
        onBack={() => navigate(-1)}
      />
      <div style={{ marginTop: '56px' }} />

      <div className='ew-p-16'>
        <div className='ew-d-flex ew-justify-content-between ew-align-items-center ew-mb-16'>
          <div>
            {data?.title ? <div className='ew-typography-extra-large'>{data.title}</div> : null}
            {data?.subtitle ? <div className=' ew-color-secondary'>{data.subtitle}</div> : null}
          </div>
          {data?.assets?.traillingIcon ? (
            <img
              alt='Payment illustration'
              height={56}
              src={data.assets.traillingIcon}
              width={56}
            />
          ) : null}
        </div>

        <RepaymentAmountList
          data={data?.sections?.items?.filter(item => item.type === 'repayment_amount')}
          onSelectionChange={handleRepaymentSelection}
        />

        <div className='ew-bottom-card ew-px-16 ew-mt-16 ew-w-100' style={{ left: '0' }}>
          <Button
            fullWidth
            isDisabled={!selectedRepayment}
            onClick={handleContinue}
            size='large'
            variant='brand'>
            {selectedRepayment ? 'Continue' : 'Select an option'}
          </Button>
        </div>
      </div>

      <ReviewPaymentBottomSheet
        data={subScreen?.[getReviewSubScreenKey(selectedRepayment?.item?.meta?.payment_type)]}
        onClose={() => setShowReviewModal(false)}
        onPay={handlePay}
        show={showReviewModal}
      />

      <form
        action={transactData?.payment_url || '/'}
        method='POST'
        ref={paymentFormRef}
        style={{ display: 'none' }}>
        {transactData
          ? getPayuFormFieldKeys(transactData).map(key => (
              <input key={key} name={key} type='hidden' value={transactData[key]} />
            ))
          : null}
      </form>
    </>
  )
}

export default PayPage
